import React, { useRef, useEffect, useLayoutEffect, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { ConfirmDialog, ContextMenu, Menu, Modal, avatarColorIndex, Tip, EmojiObject, ReplyObject, MessagesObject, MoreObject } from '@ofself/shell'
import { useChat } from '../contexts/ChatContext'
import AgentMessage from './AgentMessage'
import AuditBadge from './AuditBadge'
import { trackEmojiUsage, getFrequentEmojis } from './EmojiPickerPanel'
import ReactionPicker from './ReactionPicker'
import { formatTime, formatDayDivider } from '../utils/time'
import DayDividerCalendar from './DayDividerCalendar'
import UserAvatar from './UserAvatar'
import { getFileUrl, fetchLinkPreview, getHuddleRecordings } from '../services/api'
import { CornerUpRight, ChevronDown } from 'react-feather'
import { MailOpen, Pin } from 'lucide-react'
import { decodeAudioToSamples, drawWaveform, barsForWidth } from '../utils/audioWaveform'
import { firstVisibleMessage } from '../utils/firstVisibleMessage'

// Detect messages that are only emoji (any count, no other text)
const EMOJI_REGEX = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\p{Regional_Indicator}{2})+$/u
function isEmojiOnly(text) {
  if (!text) return false
  const stripped = text.replace(/\s/g, '')
  return stripped.length > 0 && EMOJI_REGEX.test(stripped)
}


const DEFAULT_QUICK_EMOJIS = ['\u{1F49A}', '\u{1F44D}', '\u{1F602}', '\u{1F64C}', '\u{1F389}']
const QUICK_EMOJI_COUNT = 3

/** THE quick-reaction row, for every way into it — the hover strip's inline
 *  cells and the right-click reaction row read this one function, so the two
 *  can never offer different emoji.
 *  The house defaults LEAD: a three-cell row is muscle memory, and ranking it
 *  by a running tally made it churn (one grumpy afternoon and the row greets
 *  every message with 😢👎 for a week). Your most-used (EmojiPickerPanel keeps
 *  that tally) fill whatever slots the defaults leave, and the drawer's
 *  "Frequently Used" section is where personalisation properly lives. */
export function quickReactionEmojis(count = QUICK_EMOJI_COUNT) {
  const seen = new Set()
  const out = []
  for (const emoji of [...DEFAULT_QUICK_EMOJIS, ...getFrequentEmojis()]) {
    if (!emoji || seen.has(emoji)) continue
    seen.add(emoji)
    out.push(emoji)
    if (out.length === count) break
  }
  return out
}

/* ── RUNS ────────────────────────────────────────────────────────────────
   Consecutive messages from one voice, close in time, read as a single
   utterance — so they share one head (avatar + name) and close up their
   spacing. A run is a presentation of the HEAD, never a merge of rows:
   every message keeps its own row, hover band, aside cells, reactions,
   receipts and data-message-id. The shell's aligned register consumes the
   result as data-group="head|mid|tail|solo" (@ofself/shell/chat.css §6).   */
const RUN_WINDOW_MS = 5 * 60 * 1000

// One voice = one sender, and for agents the persona behind them (several
// personas can answer through the same sender row).
const runVoice = (m) => `${m.sender_type || 'user'}:${m.sender_id || ''}:${m.agent_meta?.agent_name || ''}`

/** Do `cur` and `prev` belong to the same run? Anything that re-opens the
 *  turn — a different voice, a long pause, a quoted reply, a call card, the
 *  unread waterline — ends it. */
function sameRun(prev, cur, unreadDividerMsgId) {
  if (!prev || !cur) return false
  if (runVoice(prev) !== runVoice(cur)) return false
  if (cur.reply_to) return false
  if (prev.video_call || cur.video_call) return false
  if (unreadDividerMsgId && prev.id === unreadDividerMsgId) return false
  const dt = new Date(cur.timestamp) - new Date(prev.timestamp)
  return dt >= 0 && dt < RUN_WINDOW_MS
}

function AnimatedEmoji({ emoji, size = 20 }) {
  return <span style={{ fontSize: size * 0.85, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size }}>{emoji}</span>
}

/** The ONE reactions widget (register: .message-reactions / .reaction-chip).
 *  A wrapping chip row UNDER the message content — every reaction visible
 *  at a glance, each chip a one-click toggle, scaling to any member-times-
 *  emoji count by growing DOWN (the free direction; the prose never
 *  rewraps). Exported for ThreadView (same pattern as MessageContent). */
export function ReactionChips({ reactions, currentUserId, resolveUsername, onToggle }) {
  if (!reactions || reactions.length === 0) return null
  return (
    <div className="message-reactions">
      {reactions.map((r) => {
        const names = (r.user_ids || []).map((id) => resolveUsername(id)).join(', ')
        return (
          <Tip key={r.emoji} label={names}>
            <button
              className={`reaction-chip ${r.user_ids?.includes(currentUserId) ? 'reaction-mine' : ''}`}
              onClick={() => onToggle(r.emoji, r.user_ids)}
            >
              <AnimatedEmoji emoji={r.emoji} size={18} />
              <span className="reaction-count">{r.count}</span>
            </button>
          </Tip>
        )
      })}
    </div>
  )
}

// THE kit hash — one hue per name, app aliases map onto the kit palette.
const avatarColor = (name) => `var(--avatar-${avatarColorIndex(name)})`

const SDK_FRONTEND_URL = import.meta.env.VITE_SDK_FRONTEND_URL || 'http://localhost:3000'

/** Sidebar that shows all per-track audio recordings for a completed huddle call.
 *  Rendered by App.jsx inside the message-list-card sleeve (same pattern as ThreadView).
 *  Exported so App.jsx can import it directly.
 */
export function RecordingsSidebar({ huddleId, groupId, open, basis, separatorProps }) {
  const { closeRecordingsSidebar } = useChat()
  const [recordings, setRecordings] = useState(null) // null = loading

  useEffect(() => {
    // Don't clear on a null huddleId — keep the last tracks visible while the panel
    // eases closed (it's invisible at basis 0 anyway); only (re)load for a real huddle.
    if (!huddleId) return
    setRecordings(null)
    getHuddleRecordings(huddleId)
      .then(data => setRecordings(data.recordings || []))
      .catch(() => setRecordings([]))
  }, [huddleId])

  function nodeUrl(rec) {
    const nodeId = rec.sdk_node_id || rec.audio_raw_file_node_id
    if (!nodeId) return null
    return groupId
      ? `${SDK_FRONTEND_URL}/group/${groupId}/graph?tab=explore&node=${nodeId}`
      : `${SDK_FRONTEND_URL}/graph?tab=explore&node=${nodeId}`
  }

  function fmtDur(secs) {
    if (secs == null) return null
    const m = Math.floor(secs / 60)
    const s = Math.floor(secs % 60)
    return m ? `${m}m ${s}s` : `${s}s`
  }

  function fmtTime(iso) {
    if (!iso) return null
    try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }
    catch { return null }
  }

  return (
    // First-class shell panel (the thread's twin) — a flat .block sibling that
    // opens/closes by its width (kit-eased), folds, and resizes like every panel.
    <aside
      className="block thread block--nudge"
      style={{ flex: `0 0 ${basis ?? 0}px`, minWidth: 0 }}
      aria-hidden={open ? undefined : true}
      inert={open ? undefined : ''}
    >
      {open && separatorProps && <div className="block__resize" {...separatorProps} />}
      {/* Header — mirrors thread-header layout exactly */}
      <div className="thread-header block__title">
        <div className="block__title-surface">
        <span className="block__title-name">Recordings</span>
        <span className="thread-subtitle">
          {recordings === null ? 'Loading…' : `${recordings.length} track${recordings.length !== 1 ? 's' : ''}`}
        </span>
        <Tip label="Close">
          <button className="block__title-close" onClick={closeRecordingsSidebar} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </Tip>
        </div>
      </div>

      {/* Track list — reuses thread-messages scroll container */}
      <div className="block__body thread-messages" style={{ padding: '8px 0' }}>
        {recordings === null ? (
          <div className="recordings-track-empty">Loading recordings…</div>
        ) : recordings.length === 0 ? (
          <div className="recordings-track-empty">
            No recordings yet.
            <br />
            <span style={{ fontSize: 'var(--ofself-prose-sm)', opacity: 0.55 }}>
              Tracks upload when each participant leaves the call.
            </span>
          </div>
        ) : (
          recordings.map(rec => {
            const url = nodeUrl(rec)
            const dur = fmtDur(rec.duration_seconds)
            const timeStr = fmtTime(rec.started_at)
            const name = rec.participant_username || rec.participant_user_id?.slice(0, 8) || 'Unknown'
            const sessionLabel = rec.session_index > 0 ? ` · rejoin ${rec.session_index}` : ''

            return (
              <div key={rec.id} className="recordings-track-item">
                <div className="recordings-track-row">
                  <span className="recordings-track-name">{name}{sessionLabel}</span>
                  {dur && <span className="recordings-track-dur">{dur}</span>}
                </div>
                {timeStr && <div className="recordings-track-time">{timeStr}</div>}
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="recordings-track-link">
                    Open in Paradigm
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 3, verticalAlign: -1 }}>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                ) : (
                  <span className="recordings-track-link recordings-track-link--pending">Processing…</span>
                )}
              </div>
            )
          })
        )}
      </div>
    </aside>
  )
}

/** Slack-style huddle summary card for videocall messages */
function HuddleCard({ vc, timestamp, groupId }) {
  const { openRecordingsSidebar } = useChat()
  const duration = vc.duration_seconds
  const participants = vc.participants || []
  const names = [...new Set(participants.map(p => typeof p === 'object' ? (p.username || p.user_id?.slice(0, 8)) : p))]

  let durStr = ''
  if (duration != null) {
    const m = Math.floor(duration / 60)
    const s = duration % 60
    durStr = m ? `${m}m${s ? ` ${s}s` : ''}` : `${s}s`
  }

  return (
    <>
      <div className="huddle-card">
        <div className="huddle-card-body">
          <div className="huddle-card-title">
            Call ended{durStr ? <span className="huddle-card-dur"> · {durStr}</span> : null}
          </div>
          <div className="huddle-card-detail">
            {names.length > 0 ? names.join(', ') : 'Unknown'}{' '}
            {names.length === 1 ? 'was' : 'were'} in the call.
          </div>
          {vc.huddle_id && (
            <button
              className="huddle-card-recording-badge"
              onClick={() => openRecordingsSidebar(vc.huddle_id)}
            >
              Recordings
            </button>
          )}
        </div>
      </div>

    </>
  )
}

function AudioPlayer({ file }) {
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [samples, setSamples] = useState(null)
  const audioRef = useRef(null)
  const canvasRef = useRef(null)
  const rafRef = useRef(null)

  // Decode audio for waveform — compute bar count from canvas width
  useEffect(() => {
    let cancelled = false
    const url = getFileUrl(file.id)
    const barCount = canvasRef.current ? barsForWidth(canvasRef.current.getBoundingClientRect().width) : 80
    fetch(url)
      .then(r => r.blob())
      .then(blob => decodeAudioToSamples(blob, barCount))
      .then(({ samples: s, duration: d }) => {
        if (cancelled) return
        setSamples(s)
        setDuration(d)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [file.id])

  // Audio element
  useEffect(() => {
    const audio = new Audio(getFileUrl(file.id))
    audioRef.current = audio
    audio.addEventListener('loadedmetadata', () => { if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration) })
    audio.addEventListener('ended', () => {
      setPlaying(false)
      setProgress(0)
      setCurrentTime(0)
      cancelAnimationFrame(rafRef.current)
    })
    return () => { audio.pause(); audio.src = '' }
  }, [file.id])

  // Draw waveform
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !samples) return
    const style = getComputedStyle(document.documentElement)
    drawWaveform(canvas, samples, {
      progress,
      fill: true,
      playedColor: style.getPropertyValue('--accent')?.trim() || '#2d7ca8',
      unplayedColor: style.getPropertyValue('--text-faint')?.trim() || 'rgba(255,255,255,0.3)',
    })
  }, [samples, progress])

  function togglePlay() {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      cancelAnimationFrame(rafRef.current)
    } else {
      audio.play()
      function tick() {
        if (audio.duration) {
          setProgress(audio.currentTime / audio.duration)
          setCurrentTime(audio.currentTime)
        }
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    setPlaying(!playing)
  }

  function handleCanvasClick(e) {
    const audio = audioRef.current
    const canvas = canvasRef.current
    if (!audio || !canvas || !audio.duration) return
    const rect = canvas.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * audio.duration
    setProgress(ratio)
    setCurrentTime(audio.currentTime)
  }

  function fmt(sec) {
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  return (
    <div className="audio-player">
      <button className="audio-player-btn" onClick={togglePlay}>
        {playing ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21" /></svg>
        )}
      </button>
      <div className="audio-player-wave-wrap" onClick={handleCanvasClick}>
        {samples ? (
          <canvas ref={canvasRef} className="audio-player-canvas" />
        ) : (
          <span className="audio-player-loading">Loading...</span>
        )}
      </div>
      <span className="audio-player-time">{fmt(currentTime)} / {fmt(duration)}</span>
    </div>
  )
}

function FileCard({ file, onImageClick }) {
  const sizeStr = file.size < 1024 ? `${file.size} B`
    : file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB`
    : `${(file.size / (1024 * 1024)).toFixed(1)} MB`

  const isImage = file.mimetype?.startsWith('image/')
  const isAudio = file.mimetype?.startsWith('audio/') || file.original_filename?.match(/^voice-.*\.(webm|m4a|ogg|mp3|wav)$/i)
  const isVideo = file.mimetype?.startsWith('video/') || file.original_filename?.match(/^video-.*\.(webm|mp4|mov)$/i)

  if (isAudio) return <AudioPlayer file={file} />
  if (isVideo) return (
    <div className="video-player-inline">
      <video src={getFileUrl(file.id)} controls playsInline preload="metadata" className="video-player-element" />
    </div>
  )

  return (
    <div className="file-card">
      {isImage ? (
        <span className="file-card-frame ofself-float-plate">
          <img
            src={getFileUrl(file.id)}
            alt={file.original_filename}
            className="file-card-image"
            loading="lazy"
            decoding="async"
            onClick={() => onImageClick?.(getFileUrl(file.id), file.original_filename)}
            style={{ cursor: 'pointer' }}
          />
        </span>
      ) : (
        <div className="file-card-body">
          <div className="file-card-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
          <div className="file-card-info">
            <span className="file-card-name">{file.original_filename}</span>
            <span className="file-card-size">{sizeStr}</span>
          </div>
          <Tip label="Download">
            <a href={getFileUrl(file.id)} className="file-card-download" download>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </a>
          </Tip>
        </div>
      )}
    </div>
  )
}

export function ForwardModal({ message, channels, dmChannels, onForward, onClose }) {
  const [search, setSearch] = useState('')
  const allTargets = [
    ...channels.map(ch => ({ id: ch.id, name: `# ${ch.name}`, type: 'channel' })),
    ...dmChannels.map(dm => ({ id: dm.id, name: dm.other_username || dm.name, type: 'dm' }))
  ]
  const filtered = search.trim()
    ? allTargets.filter(t => t.name.toLowerCase().includes(search.toLowerCase()))
    : allTargets

  return (
    <Modal onClose={onClose} title="Forward message" size="md" className="forward-modal">
          <div className="forward-preview">
            <span className="forward-preview-sender">{message.sender?.username}</span>
            <span className="forward-preview-content">
              {message.content.length > 80 ? message.content.slice(0, 80) + '...' : message.content}
            </span>
          </div>
          <input
            className="modal-input"
            type="text"
            placeholder="Search channels or DMs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <ul className="forward-list">
            {filtered.map(target => (
              <li key={target.id}>
                <button className="forward-list-item" onClick={() => onForward(target.id, message)}>
                  <span className="forward-list-icon">
                    {target.type === 'channel' ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 9h16M4 15h16M10 3l-2 18M16 3l-2 18" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    )}
                  </span>
                  <span>{target.name}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="forward-list-empty">No matches found</li>
            )}
          </ul>
          <div className="modal-actions">
            <button type="button" className="modal-btn modal-btn-cancel" onClick={onClose}>Cancel</button>
          </div>
    </Modal>
  )
}

/** The one "Delete message?" (the conversation's rows, a voice row, the thread's): the shell's ConfirmDialog, the
 *  words (or the voice) it would take shown before it goes. */
export function DeleteMessageConfirm({ msg, open, onCancel, onConfirm }) {
  const voice = msg.file && (msg.file.mimetype?.startsWith('audio/') || msg.file.original_filename?.match(/^voice-/))
  return (
    <ConfirmDialog open={open} title="Delete message?" confirmLabel="Delete" danger onConfirm={onConfirm} onCancel={onCancel}>
      <div className="delete-confirm-preview">
        {voice ? <AudioPlayer file={msg.file} /> : (
          <span className="delete-confirm-content">{msg.content.length > 120 ? msg.content.slice(0, 120) + '...' : msg.content}</span>
        )}
      </div>
      This action cannot be undone.
    </ConfirmDialog>
  )
}

function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    function handleKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <img src={src} alt={alt} className="lightbox-image" onClick={e => e.stopPropagation()} />
    </div>
  )
}

function PinnedMessagesPanel({ pinnedMessages, onUnpin, onScrollTo, onForwardClick, openThread, resolveUsername }) {
  const [showAll, setShowAll] = useState(false)
  const [ctxMenu, setCtxMenu] = useState(null)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!ctxMenu) return
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setCtxMenu(null) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [ctxMenu])

  const latestPin = pinnedMessages[pinnedMessages.length - 1]
  const latestMsg = latestPin?.message
  const latestSender = latestMsg?.sender?.username || resolveUsername(latestMsg?.sender_id) || 'Unknown'
  const latestContent = latestMsg?.content || ''
  const count = pinnedMessages.length

  const renderPinItem = (pin) => {
    const msg = pin.message
    const sender = msg?.sender?.username || resolveUsername(msg?.sender_id) || 'Unknown'
    const content = msg?.content || ''
    const isAudio = msg?.file && (msg.file.mimetype?.startsWith('audio/') || msg.file.original_filename?.match(/^voice-/))
    const hideText = isAudio && (!content || /^(Voice message|Audio clip|Shared a file)$/i.test(content.trim()))
    return (
      <div
        key={pin.id}
        className="pinned-message-item"
        onClick={(e) => { if (!e.target.closest('.audio-player')) { onScrollTo(pin.message_id); setShowAll(false) } }}
        onContextMenu={e => {
          e.preventDefault()
          const x = Math.min(e.clientX, window.innerWidth - 200)
          const y = Math.min(e.clientY, window.innerHeight - 200)
          setCtxMenu({ x, y, pin })
        }}
      >
        <Pin size={12} className="pinned-message-pin-icon" />
        <div className="pinned-message-body">
          <span className="pinned-message-sender">{sender}</span>
          {!hideText && <span className="pinned-message-content">{content.length > 120 ? content.slice(0, 120) + '...' : content}</span>}
          {isAudio && <AudioPlayer file={msg.file} />}
        </div>
      </div>
    )
  }

  return (
    <div className="pinned-messages-panel">
      {/* Single banner showing latest pin */}
      {!showAll && (() => {
        const msg = latestMsg
        const isAudio = msg?.file && (msg.file.mimetype?.startsWith('audio/') || msg.file.original_filename?.match(/^voice-/))
        const hideText = isAudio && (!latestContent || /^(Voice message|Audio clip|Shared a file)$/i.test(latestContent.trim()))
        return (
          <div
            className="pinned-message-item"
            onClick={(e) => { if (!e.target.closest('.audio-player')) onScrollTo(latestPin.message_id) }}
            onContextMenu={e => {
              e.preventDefault()
              const x = Math.min(e.clientX, window.innerWidth - 200)
              const y = Math.min(e.clientY, window.innerHeight - 200)
              setCtxMenu({ x, y, pin: latestPin })
            }}
          >
            <Pin size={12} className="pinned-message-pin-icon" />
            <div className="pinned-message-body">
              <span className="pinned-message-sender">{latestSender}</span>
              {!hideText && <span className="pinned-message-content">{latestContent.length > 120 ? latestContent.slice(0, 120) + '...' : latestContent}</span>}
              {isAudio && <AudioPlayer file={msg.file} />}
            </div>
            {count > 1 && (
              <Tip label="View all pinned messages">
                <button
                  className="pinned-message-count-badge"
                  onClick={(e) => { e.stopPropagation(); setShowAll(true) }}
                >
                  {count}
                </button>
              </Tip>
            )}
          </div>
        )
      })()}

      {/* Expanded list of all pins */}
      {showAll && (
        <>
          <div className="pinned-messages-header">
            <Pin size={12} className="pinned-message-pin-icon" />
            <span className="pinned-messages-header-title">{count} Pinned Messages</span>
            <button className="pinned-messages-close" onClick={() => setShowAll(false)}>&times;</button>
          </div>
          {pinnedMessages.map(renderPinItem)}
        </>
      )}

      {ctxMenu && createPortal(
        <div ref={menuRef} className="msg-context-menu" style={{ position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 1000 }}>
          <div className="msg-context-actions">
            <button className="ofself-menu-item" onClick={() => { onScrollTo(ctxMenu.pin.message_id); setCtxMenu(null); setShowAll(false) }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              Go to message
            </button>
            {ctxMenu.pin.message && (
              <button className="ofself-menu-item" onClick={() => { openThread(ctxMenu.pin.message); setCtxMenu(null) }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                Open Thread
              </button>
            )}
            <button className="ofself-menu-item" onClick={() => { navigator.clipboard.writeText(ctxMenu.pin.message?.content || ''); setCtxMenu(null) }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy Text
            </button>
            {ctxMenu.pin.message && (
              <button className="ofself-menu-item" onClick={() => { onForwardClick(ctxMenu.pin.message); setCtxMenu(null) }}>
                <CornerUpRight size={14} />
                Forward
              </button>
            )}
            <button className="ofself-menu-item ofself-menu-item--danger" onClick={() => { onUnpin(ctxMenu.pin.message_id); setCtxMenu(null) }}>
              <Pin size={14} />
              Unpin
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

function DoubleCheck({ read, readBy, unreadBy }) {
  const tooltip = read
    ? `Read by ${(readBy || []).join(', ')}`
    : `Delivered${(unreadBy || []).length ? '\nNot yet read by ' + unreadBy.join(', ') : ''}`
  return (
    <Tip label={tooltip}>
      <span className={`receipt-checks${read ? ' receipt-read' : ''}`}>
        <svg width="16" height="10" viewBox="0 0 16 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1 5.5 3.5 8 8.5 2" />
          <polyline points="5.5 5.5 8 8 13 2" />
        </svg>
      </span>
    </Tip>
  )
}

const URL_RE = /https?:\/\/[^\s<]+[^\s<.,;:!?"'\])}>]/g
const previewCache = {}

function LinkPreviewCard({ url }) {
  const [preview, setPreview] = useState(previewCache[url] || null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (previewCache[url]) { setPreview(previewCache[url]); return }
    if (failed) return
    let cancelled = false
    fetchLinkPreview(url).then(data => {
      if (cancelled) return
      if (data.title || data.image) {
        previewCache[url] = data
        setPreview(data)
      } else {
        setFailed(true)
      }
    }).catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [url, failed])

  if (!preview || (!preview.title && !preview.image)) return null

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="link-preview-card">
      {preview.image && (
        <img src={preview.image} alt="" className="link-preview-image" onError={e => e.target.style.display = 'none'} />
      )}
      <div className="link-preview-info">
        {preview.site_name && <span className="link-preview-site">{preview.site_name}</span>}
        {preview.title && <span className="link-preview-title">{preview.title}</span>}
        {preview.description && <span className="link-preview-desc">{preview.description.slice(0, 150)}</span>}
      </div>
    </a>
  )
}

/* "Not sent": the send failed (offline, the server said no). The words stay in
   the stream until you send them again or let them go. Its own component so only
   an unsent row subscribes to the chat context. */
export function UnsentNote({ msg }) {
  const { retryUnsent, discardUnsent } = useChat()
  return (
    <div className="message-unsent" role="status">
      <span className="message-unsent-label">Not sent</span>
      <button type="button" className="message-unsent-btn" onClick={() => retryUnsent(msg)}>Retry</button>
      <button type="button" className="message-unsent-btn" onClick={() => discardUnsent(msg)}>Discard</button>
    </div>
  )
}

function MessageLinkPreviews({ content }) {
  const urls = useMemo(() => {
    const matches = content?.match(URL_RE) || []
    return [...new Set(matches)].slice(0, 3)
  }, [content])
  if (urls.length === 0) return null
  return <div className="link-previews">{urls.map(url => <LinkPreviewCard key={url} url={url} />)}</div>
}

function MessageRow({ msg, idx, group, quickEmojis, pickerMsgId, setPickerMsgId, onReactionClick, onPickerSelect, openThread, currentUser, onImageClick, onForwardClick, onMarkUnread, onStartEdit, onDeleteMessage, onPinMessage, pinnedMessageIds, resolveUsername, onReply, onScrollToMessage, receiptStatus, activeChannel, justReacted, justArrived }) {

  const isOwn = msg.sender_id === currentUser?.id
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Right-click menu (shell <ContextMenu>) — reactions row + actions, declaratively.
  const reactionEntries = [
    ...quickEmojis.map((emoji) => ({ content: <AnimatedEmoji emoji={emoji} size={22} animate="always" />, onSelect: () => onReactionClick(msg.id, emoji, []) })),
    { className: 'ofself-menu-reaction--more', content: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>, onSelect: () => setPickerMsgId(msg.id) },
  ]
  const copyItem = { label: 'Copy Text', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>, onSelect: () => navigator.clipboard.writeText(msg.content) }
  // An unsent message exists only here: nothing the server knows about can be done to it.
  const menuItems = msg._unsent ? [copyItem] : [
    { label: 'Reply', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>, onSelect: () => onReply?.(msg) },
    { label: 'Open Thread', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>, onSelect: () => openThread(msg) },
    copyItem,
    isOwn && { label: 'Edit', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>, onSelect: () => onStartEdit(msg) },
    msg.file && { label: 'Save', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>, onSelect: () => { const a = document.createElement('a'); a.href = getFileUrl(msg.file.id); a.download = msg.file.original_filename || ''; document.body.appendChild(a); a.click(); a.remove() } },
    { label: pinnedMessageIds?.has(msg.id) ? 'Unpin' : 'Pin', icon: <Pin size={14} />, onSelect: () => onPinMessage(msg.id) },
    { label: 'Forward', icon: <CornerUpRight size={14} />, onSelect: () => onForwardClick(msg) },
    { label: 'Mark as unread', icon: <MailOpen size={14} />, onSelect: () => onMarkUnread(msg) },
    isOwn && { label: 'Delete', danger: true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>, onSelect: () => setConfirmDelete(true) },
  ]

  // Videocall messages render with sender info + huddle card
  if (msg.video_call) {
    return (
      <div className="block__row message" data-message-id={msg.id} data-group={group}>
        <UserAvatar sender={msg.sender} className="message-avatar" />
        <div className="message-body">
          <div className="message-meta">
            <span className="message-sender">{msg.sender?.username || 'Unknown'}</span>
            <span className="message-time">{formatTime(msg.timestamp)}</span>
          </div>
          <HuddleCard vc={msg.video_call} timestamp={msg.timestamp} groupId={activeChannel?.group_id} />
        </div>
      </div>
    )
  }

  if (msg.sender_type === 'agent' || msg.sender_type === 'plugin') {
    return (
      <ContextMenu reactions={reactionEntries} items={menuItems} trigger={(
      <div className={`block__row message agent-message${justArrived ? ' message-arrived' : ''}`} data-message-id={msg.id} data-group={group}>
        <AgentMessage message={msg} />
        {/* Agent rows carry no hover strip, so the drawer hangs off the row
            itself — the right-click menu's "more" still has somewhere to open. */}
        <ReactionPicker
          open={pickerMsgId === msg.id}
          onOpenChange={(o) => setPickerMsgId(o ? msg.id : null)}
          onSelect={(emoji) => onPickerSelect(msg.id, emoji)}
        />
        <DeleteMessageConfirm msg={msg} open={confirmDelete} onCancel={() => setConfirmDelete(false)} onConfirm={() => { onDeleteMessage(msg.id); setConfirmDelete(false) }} />
      </div>
      )} />
    )
  }

  return (
    <ContextMenu reactions={msg._unsent ? [] : reactionEntries} items={menuItems} trigger={(
    <div className={`block__row message${msg._unsent ? ' message--unsent' : ''}${justReacted ? ' message-just-reacted' : ''}${justArrived ? ' message-arrived' : ''}`} data-message-id={msg.id} data-group={group}>
      <UserAvatar sender={msg.sender} className="message-avatar" />
      <div className="message-body">
        <div className="message-meta">
          <span className="message-sender">{msg.sender?.username}</span>
          {msg.auditTrail && <AuditBadge />}
          {/* time sits next to the name (matches the thread). Grouped follow-ups
              hide the whole meta, so they fall back to the left-gutter hover time below. */}
          <span className="message-time">
            {receiptStatus && <DoubleCheck read={receiptStatus.isRead} readBy={receiptStatus.readBy} unreadBy={receiptStatus.unreadBy} />}
            {formatTime(msg.timestamp)}
          </span>
        </div>
        {msg.reply_to && (
          <button
            className="inline-reply-preview"
            onClick={() => onScrollToMessage?.(msg.reply_to.id)}
          >
            <span className="inline-reply-content">
              <span className="inline-reply-sender">{msg.reply_to.sender_name}</span>
              <span className="inline-reply-text">
                {msg.reply_to.content.length > 80 ? msg.reply_to.content.slice(0, 80) + '...' : msg.reply_to.content}
              </span>
            </span>
          </button>
        )}
        {msg.video_call ? (
          <HuddleCard vc={msg.video_call} timestamp={msg.timestamp} groupId={activeChannel?.group_id} />
        ) : (() => {
          const hideText = !!msg.file && (!msg.content || /^(Voice message|Audio clip|Video clip|Shared a file)$/i.test(msg.content.trim()))
          return (
            <>
              {!hideText && (
                <div className={`message-content${isEmojiOnly(msg.content) ? ' emoji-only' : ''}`}>
                  <MessageContent content={msg.content} />
                  {msg.edited && <span className="message-edited-tag">(edited)</span>}
                  <span className="bubble-time">{formatTime(msg.timestamp)}{receiptStatus && <DoubleCheck read={receiptStatus.isRead} readBy={receiptStatus.readBy} unreadBy={receiptStatus.unreadBy} />}</span>
                </div>
              )}
              {msg.file && <FileCard file={msg.file} onImageClick={onImageClick} />}
            </>
          )
        })()}
        {msg._unsent && <UnsentNote msg={msg} />}
        <MessageLinkPreviews content={msg.content} />
        <ReactionChips
          reactions={msg.reactions}
          currentUserId={currentUser?.id}
          resolveUsername={resolveUsername}
          onToggle={(emoji, userIds) => onReactionClick(msg.id, emoji, userIds)}
        />
        {/* The reply chip rides below the content too — same reasoning as
            the reactions: it is content-adjacent signal, and DOWN is the
            free direction. Avatars beside the count, the whole chip a
            click into the thread. */}
        {msg.reply_count > 0 && (
          <button
            className="thread-indicator"
            onClick={(e) => { e.stopPropagation(); openThread(msg); }}
          >
            <span className="thread-indicator-avatars">
              {(msg.reply_authors || []).slice(0, 3).map((author, i) => {
                const name = author.username || '?'
                return (
                  <UserAvatar
                    key={author.id}
                    userID={author.id}
                    avatarUrl={author.avatar_url}
                    initials={name[0].toUpperCase()}
                    color={avatarColor(name)}
                    className="thread-indicator-avatar"
                    style={{ zIndex: 3 - i }}
                  />
                )
              })}
            </span>
            <span className="thread-indicator-count">{msg.reply_count} {msg.reply_count === 1 ? 'reply' : 'replies'}</span>
          </button>
        )}
      </div>
      {/* The aside anchors the hover-action STRIP — quick reactions · react ·
          reply · thread · ⋯, centred beside the message (register:
          .message-actions). The ⋯ menu is the SAME menuItems the right-click
          ContextMenu renders: one declaration, three ways in. */}
      <div className="message-aside">
        <div className="message-actions ofself-plate--sm">
          {/* The quick row — one tap, no menu. The SAME list the right-click
              reaction row offers (quickReactionEmojis), so the two ways in
              never drift apart. */}
          {quickEmojis.map((emoji) => (
            <Tip key={emoji} label={`React ${emoji}`}>
              <button
                className="message-action-cell message-action-cell--emoji"
                aria-label={`React ${emoji}`}
                onClick={() => onReactionClick(msg.id, emoji, msg.reactions?.find(r => r.emoji === emoji)?.user_ids || [])}
              >
                <AnimatedEmoji emoji={emoji} size={18} />
              </button>
            </Tip>
          ))}
          {/* + reaction — the drawer itself, one click. (It used to open a menu
              that merely repeated the three quick cells sitting beside it.) */}
          <ReactionPicker
            open={pickerMsgId === msg.id}
            onOpenChange={(o) => setPickerMsgId(o ? msg.id : null)}
            onSelect={(emoji) => onPickerSelect(msg.id, emoji)}
            align="end"
            trigger={(
              <button className="message-action-cell" aria-label="React">
                <EmojiObject />
              </button>
            )}
          />
          <Tip label="Reply">
            <button
              className="message-action-cell"
              aria-label="Reply"
              onClick={() => onReply?.(msg)}
            >
              <ReplyObject />
            </button>
          </Tip>
          <Tip label="Reply in thread">
            <button
              className="message-action-cell"
              aria-label="Reply in thread"
              onClick={(e) => { e.stopPropagation(); openThread(msg); }}
            >
              <MessagesObject />
            </button>
          </Tip>
          <Menu
            trigger={(
              <button className="message-action-cell" aria-label="More">
                <MoreObject />
              </button>
            )}
            items={menuItems}
          />
        </div>
      </div>
      <DeleteMessageConfirm msg={msg} open={confirmDelete} onCancel={() => setConfirmDelete(false)} onConfirm={() => { onDeleteMessage(msg.id); setConfirmDelete(false) }} />
    </div>
    )} />
  )
}

export default function MessageList() {
  const {
    activeMessages, openThread, addReaction, removeReaction, currentUser,
    forwardMessage, channels, dmChannels, activeHuddle, readReceipts, activeChannelMembers,
    activeChannel, unreadDividerMsgId, members, typingUsers, markAsUnread,
    editMessage, deleteMessage, setEditingMessage,
    pinnedMessages, pinMessage, unpinMessage,
    setReplyingToMessage,
    hasMoreMessages, loadingOlder, loadOlderMessages,
    bulkEditMode, setBulkEditMode, bulkSelectedMessages, setBulkSelectedMessages,
    jumpToMessageId, setJumpToMessageId
  } = useChat()
  const bottomRef = useRef(null)
  const [justReactedMsgId, setJustReactedMsgId] = useState(null)
  const flashTimeoutRef = useRef(null)
  useEffect(() => () => clearTimeout(flashTimeoutRef.current), [])


  // Jump-to-message: scroll and highlight when jumpToMessageId is set (from search)
  useEffect(() => {
    if (!jumpToMessageId) return
    // Small delay to let messages render after channel switch
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-message-id="${jumpToMessageId}"]`)
      if (el) jumpToMessageEl(el)
      setJumpToMessageId(null)
    }, 300)
    return () => clearTimeout(timer)
  }, [jumpToMessageId, activeMessages, setJumpToMessageId])

  // The layout is applied to the document by chatLayout.js (at boot and on
  // change); the list only needs to know whether bubbles are on, for the
  // one-to-one DM variant below.
  const bubbleMode = document.documentElement.getAttribute('data-chat-layout') === 'bubbles'

  // The quick row, resolved ONCE per list render rather than per message —
  // it reads localStorage, and every row would otherwise re-parse the tally.
  // activeMessages in the deps is deliberate: reacting re-renders the list,
  // which is exactly when the frequency order may have moved.
  const quickEmojis = useMemo(() => quickReactionEmojis(), [activeMessages])

  const [pickerMsgId, setPickerMsgId] = useState(null)
  const [lightbox, setLightbox] = useState(null)
  const [forwardMsg, setForwardMsg] = useState(null)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const prevMsgCountRef = useRef(0)
  const prevLastMsgIdRef = useRef(null)

  const isInHuddle = activeHuddle?.participants?.some(p => p.user_id === currentUser?.id)

  const listRef = useRef(null)
  const restoredScrollRef = useRef(false)
  const scrollSaveTimerRef = useRef(null)
  const prevScrollHeightRef = useRef(null) // for manual scroll preservation on prepend
  const anchorMsgRef = useRef(null) // { id, offsetFromContainerTop } — message to anchor viewport on during prepend
  const isLoadingOlderRef = useRef(false) // guard against pagination re-trigger loop
  // Live snapshot of scroll position — updated on every scroll event so we can
  // capture it BEFORE React re-renders the DOM on channel switch
  const liveScrollSnapshotRef = useRef(null) // { channelId, atBottom, messageId, offsetFromTop }
  // Mark a programmatic scroll write so the scroll handler skips snapshot/save.
  // Wraps any direct `el.scrollTop = ...` we issue (restore, prepend preserve,
  // pagination, etc.). Without this, the resulting scroll event would overwrite
  // the saved position with the just-restored (possibly imprecise) coordinates,
  // and drift compounds across visits.
  function _markProgrammaticScroll() {
    programmaticScrollRef.current = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { programmaticScrollRef.current = false })
    })
  }
  // iMessage-style bubble wiggle — velocity-driven transform on .message
  // children. scroll handler feeds velocity; rAF decays it so bubbles
  // spring back when scrolling stops.
  const scrollVelRef = useRef(0)
  const lastScrollRef = useRef({ y: 0, t: 0 })
  const velRafRef = useRef(null)
  const scrollCooldownRef = useRef(false) // brief cooldown after prepend to let browser settle
  const pendingRestoreRef = useRef(null) // { messageId, offsetFromTop } — for cross-pagination restore
  const wasAtBottomRef = useRef(true) // tracks if user was at bottom (for auto-scroll on resize)
  // Track which message IDs have already been rendered — only NEW ones
  // (arriving after the channel is mounted) get the .message-arrived
  // entry animation. On channel switch we bulk-seed so nothing animates
  // during the initial batch render, and prepended (paginated) history
  // is treated as already-seen to avoid animations when scrolling up.
  const seenMsgIdsRef = useRef(new Set())
  const seenChannelIdRef = useRef(null)
  const arrivalLastIdRef = useRef(null)   // last appended id from the prior render
  const [arrivingIds, setArrivingIds] = useState(() => new Set())

  // Cancel any pending velocity rAF on unmount.
  useEffect(() => () => {
    if (velRafRef.current) {
      cancelAnimationFrame(velRafRef.current)
      velRafRef.current = null
    }
  }, [])

  // Detect newly arrived messages so we can give them a soft entry
  // animation. We only animate the tail — messages appended AFTER the
  // previous last-id. Prepended history (pagination) and the initial
  // load on channel switch are marked seen without animating.
  useLayoutEffect(() => {
    const seen = seenMsgIdsRef.current
    // Use a stable identity that survives the temp→real swap: if a
    // message was sent optimistically it keeps _key = the original
    // temp id even after the server echo replaces its id. Without
    // this, the real-id message would look brand-new and re-animate.
    const identity = m => m?._key || m?.id
    // Channel switch → seed all current ids, skip animation.
    if (seenChannelIdRef.current !== activeChannel?.id) {
      seen.clear()
      for (const m of activeMessages) {
        const k = identity(m)
        if (k) seen.add(k)
      }
      seenChannelIdRef.current = activeChannel?.id
      arrivalLastIdRef.current = identity(activeMessages[activeMessages.length - 1]) || null
      return
    }
    // Same channel: find the prior tail id in the new list; anything
    // after it is considered just-arrived.
    const prevLastId = arrivalLastIdRef.current
    const prevLastIdx = prevLastId ? activeMessages.findIndex(m => identity(m) === prevLastId) : -1
    const newlyArrivedKeys = []
    for (let i = prevLastIdx + 1; i < activeMessages.length; i++) {
      const m = activeMessages[i]
      const k = identity(m)
      if (k && !seen.has(k)) {
        newlyArrivedKeys.push(k)
      }
    }
    // Mark every current id/key as seen (including prepended history).
    for (const m of activeMessages) {
      const k = identity(m)
      if (k) seen.add(k)
    }
    arrivalLastIdRef.current = identity(activeMessages[activeMessages.length - 1]) || null

    if (newlyArrivedKeys.length === 0) return
    setArrivingIds(prev => {
      const next = new Set(prev)
      for (const k of newlyArrivedKeys) next.add(k)
      return next
    })
    const t = setTimeout(() => {
      setArrivingIds(prev => {
        const next = new Set(prev)
        for (const k of newlyArrivedKeys) next.delete(k)
        return next
      })
    }, 600)
    return () => clearTimeout(t)
  }, [activeMessages, activeChannel?.id])

  // Auto-scroll to bottom when content height changes (e.g., images loading, new messages rendering)
  // Uses MutationObserver to detect ANY DOM change inside the message list, then checks if
  // scrollHeight changed. Only scrolls if user was already at the bottom.
  // ResizeObserver on the scroll container doesn't work because the container has fixed height —
  // only the scrollable content grows when images load.
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    let lastScrollHeight = el.scrollHeight
    const observer = new MutationObserver(() => {
      // Only a list that follows its foot needs the height: reading it forces a
      // layout of the whole list, and every hover, chip and page of history
      // mutates the subtree (audit 2026-10-02, paging up a long conversation)
      if (!wasAtBottomRef.current || pendingRestoreRef.current) return
      if (el.scrollHeight === lastScrollHeight) return
      lastScrollHeight = el.scrollHeight
      _markProgrammaticScroll()
      el.scrollTop = el.scrollHeight
    })
    observer.observe(el, { childList: true, subtree: true, attributes: true, characterData: true })
    // Also handle image loads specifically (MutationObserver might miss them)
    const handleImageLoad = () => {
      if (wasAtBottomRef.current && !pendingRestoreRef.current) {
        _markProgrammaticScroll()
        el.scrollTop = el.scrollHeight
      }
    }
    el.addEventListener('load', handleImageLoad, true) // capture phase to catch img loads
    return () => {
      observer.disconnect()
      el.removeEventListener('load', handleImageLoad, true)
    }
  }, [activeChannel?.id])

  // rAF-driven ease-out tween — gives a consistent, slower, visibly
  // flowy scroll regardless of distance. Browser-native smooth often
  // snaps for short deltas.
  const smoothScrollRafRef = useRef(null)
  const programmaticScrollRef = useRef(false)
  function tweenScrollTo(el, targetTop, duration = 450) {
    if (!el) return
    if (smoothScrollRafRef.current) cancelAnimationFrame(smoothScrollRafRef.current)
    const startTop = el.scrollTop
    const delta = targetTop - startTop
    if (Math.abs(delta) < 1) { el.scrollTop = targetTop; return }
    const start = performance.now()
    programmaticScrollRef.current = true
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration)
      // easeOutBack — overshoots past 1 then settles, giving a
      // rubber-band feel on scroll landing.
      const c1 = 1.70158
      const c3 = c1 + 1
      const eased = 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
      el.scrollTop = startTop + delta * eased
      if (t < 1) {
        smoothScrollRafRef.current = requestAnimationFrame(step)
      } else {
        smoothScrollRafRef.current = null
        // Reset velocity and flag the moment the tween lands so the
        // jelly wiggle doesn't overshoot from a stale vel reading.
        scrollVelRef.current = 0
        lastScrollRef.current = { y: el.scrollTop, t: performance.now() }
        if (el.style) el.style.setProperty('--scroll-vel', '0')
        programmaticScrollRef.current = false
      }
    }
    smoothScrollRafRef.current = requestAnimationFrame(step)
  }

  // Jump to + highlight a message by CENTERING IT IN THE LIST'S OWN SCROLLER. Never
  // el.scrollIntoView() — that walks UP and scrolls every scrollable ancestor to satisfy
  // block:'center', including the canvas viewport (.shell__viewport), which pans the whole
  // board and nudges --cam-y. We compute the target scrollTop inside listRef (same rect-delta
  // convention as the anchor/restore logic) and tween to it, touching nothing above the list.
  function jumpToMessageEl(el) {
    const scroller = listRef.current
    if (!scroller || !el) return
    const sr = scroller.getBoundingClientRect()
    const er = el.getBoundingClientRect()
    const target = scroller.scrollTop + (er.top - sr.top) - (scroller.clientHeight - er.height) / 2
    const max = scroller.scrollHeight - scroller.clientHeight
    tweenScrollTo(scroller, Math.max(0, Math.min(target, max)))
    el.classList.add('message-highlight')
    setTimeout(() => el.classList.remove('message-highlight'), 2000)
  }

  function scrollToBottom(smooth) {
    const el = listRef.current || bottomRef.current?.parentElement
    if (el) {
      if (smooth) {
        tweenScrollTo(el, el.scrollHeight - el.clientHeight)
      } else {
        _markProgrammaticScroll()
        el.scrollTop = el.scrollHeight
      }
      wasAtBottomRef.current = true
      // Eagerly set live snapshot so channel switch saves "atBottom" even if
      // user never scrolls (clicks away immediately after channel loads)
      if (activeChannel?.id) {
        liveScrollSnapshotRef.current = { channelId: activeChannel.id, atBottom: true }
      }
    }
  }

  // Scroll to the unread divider element if it exists
  function scrollToUnreadDivider() {
    const el = listRef.current
    if (!el) return false
    const divider = el.querySelector('.unread-divider')
    if (!divider) {
      return false
    }
    const containerRect = el.getBoundingClientRect()
    const dividerRect = divider.getBoundingClientRect()
    // Position the divider ~1/3 from the top of the viewport
    const targetOffset = containerRect.height * 0.33
    _markProgrammaticScroll()
    el.scrollTop += dividerRect.top - containerRect.top - targetOffset
    wasAtBottomRef.current = false
    // Set snapshot so quick channel switches preserve this position
    if (activeChannel?.id) {
      const msgs = el.querySelectorAll('[data-message-id]')
      for (const msgEl of msgs) {
        const rect = msgEl.getBoundingClientRect()
        if (rect.top >= containerRect.top - 20) {
          liveScrollSnapshotRef.current = {
            channelId: activeChannel.id,
            atBottom: false,
            messageId: msgEl.getAttribute('data-message-id'),
            offsetFromTop: rect.top - containerRect.top
          }
          break
        }
      }
    }
    return true
  }

  // Save the first visible message ID for a specific channel
  function saveScrollPositionForChannel(channelId) {
    const el = listRef.current
    if (!el || !channelId) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50
    const key = `paradigm_scroll_${channelId}`
    try {
      if (atBottom) {
        sessionStorage.setItem(key, JSON.stringify({ atBottom: true }))
      } else {
        const first = firstVisibleMessage(el)
        if (first) sessionStorage.setItem(key, JSON.stringify({ atBottom: false, ...first }))
      }
    } catch {}
  }

  // Save scroll position for the current active channel (used in debounced scroll handler)
  function saveScrollPosition() {
    saveScrollPositionForChannel(activeChannel?.id)
  }

  // Restore scroll to the saved message ID
  function restoreScrollPosition() {
    const el = listRef.current
    if (!el || !activeChannel?.id) return false
    try {
      const raw = sessionStorage.getItem(`paradigm_scroll_${activeChannel.id}`)
      if (!raw) {
        return false
      }
      const saved = JSON.parse(raw)
      if (saved.atBottom) {
        _markProgrammaticScroll()
        el.scrollTop = el.scrollHeight
        wasAtBottomRef.current = true
        liveScrollSnapshotRef.current = { channelId: activeChannel.id, atBottom: true }
        return true
      }
      if (saved.messageId) {
        const msgEl = el.querySelector(`[data-message-id="${saved.messageId}"]`)
        if (msgEl) {
          const containerRect = el.getBoundingClientRect()
          const msgRect = msgEl.getBoundingClientRect()
          const offset = saved.offsetFromTop || 0
          _markProgrammaticScroll()
          el.scrollTop += msgRect.top - containerRect.top - offset
          wasAtBottomRef.current = false
          // Eagerly set snapshot so quick channel switches don't lose this position
          liveScrollSnapshotRef.current = {
            channelId: activeChannel.id,
            atBottom: false,
            messageId: saved.messageId,
            offsetFromTop: offset
          }
          return true
        }
        // Message not in DOM yet — set pending restore so pagination can find it
        const allMsgEls = el.querySelectorAll('[data-message-id]')
        // If there's no more messages to load (all history already in cache/DOM),
        // the message was probably deleted or ID changed. Just go to bottom.
        if (!hasMoreMessages) {
          scrollToBottom()
          return true
        }
        pendingRestoreRef.current = { messageId: saved.messageId, offsetFromTop: saved.offsetFromTop || 0 }
        // Scroll to bottom first so the user sees latest messages while we paginate.
        // Much better UX than showing "Beginning of conversation".
        scrollToBottom()
        // Kick off pagination to start loading toward the target
        setTimeout(() => {
          if (pendingRestoreRef.current && hasMoreMessages && !isLoadingOlderRef.current) {
            const innerEl = listRef.current
            if (innerEl) {
              prevScrollHeightRef.current = innerEl.scrollHeight
              const firstVis = innerEl.querySelector('[data-message-id]')
              if (firstVis) {
                anchorMsgRef.current = {
                  id: firstVis.getAttribute('data-message-id'),
                  offsetTop: firstVis.getBoundingClientRect().top - innerEl.getBoundingClientRect().top
                }
              }
            }
            isLoadingOlderRef.current = true
            loadOlderMessages()
          }
        }, 50)
        return true // prevent fallthrough to scrollToBottom
      }
    } catch {}
    return false
  }

  // Only scroll to bottom when messages actually change (new message or channel switch),
  // not on unrelated context re-renders (e.g. thread open/close) or older message prepend
  const msgCount = activeMessages.length
  const lastMsgId = activeMessages[activeMessages.length - 1]?.id
  const firstMsgId = activeMessages[0]?.id
  const prevChannelIdRef = useRef(activeChannel?.id)
  const prevFirstMsgIdRef = useRef(null)
  useLayoutEffect(() => {
    const channelChanged = activeChannel?.id !== prevChannelIdRef.current
    // Detect if messages were prepended (older messages loaded) — first ID changed but last didn't
    const wasPrepend = !channelChanged && firstMsgId !== prevFirstMsgIdRef.current && lastMsgId === prevLastMsgIdRef.current

    if (wasPrepend) {
      // Manual scroll preservation: keep viewport locked on the same message
      const el = listRef.current
      // Strategy 1: Use anchor message (more reliable — immune to spinner height changes)
      if (el && anchorMsgRef.current) {
        const anchorEl = el.querySelector(`[data-message-id="${anchorMsgRef.current.id}"]`)
        if (anchorEl) {
          const currentOffset = anchorEl.getBoundingClientRect().top - el.getBoundingClientRect().top
          const correction = currentOffset - anchorMsgRef.current.offsetTop
          _markProgrammaticScroll()
          el.scrollTop += correction
        }
        anchorMsgRef.current = null
      } else if (el && prevScrollHeightRef.current != null) {
        // Strategy 2: Fallback to scrollHeight delta
        const delta = el.scrollHeight - prevScrollHeightRef.current
        _markProgrammaticScroll()
        el.scrollTop = el.scrollTop + delta
      }
      prevScrollHeightRef.current = null

      // Reset pagination guard and set cooldown to prevent immediate re-trigger
      isLoadingOlderRef.current = false
      scrollCooldownRef.current = true
      setTimeout(() => {
        scrollCooldownRef.current = false
      }, 200)

      // Check if a pending restore target is now in the DOM
      if (pendingRestoreRef.current && el) {
        const { messageId, offsetFromTop } = pendingRestoreRef.current
        const msgEl = el.querySelector(`[data-message-id="${messageId}"]`)
        if (msgEl) {
          const containerRect = el.getBoundingClientRect()
          const msgRect = msgEl.getBoundingClientRect()
          _markProgrammaticScroll()
          el.scrollTop += msgRect.top - containerRect.top - (offsetFromTop || 0)
          pendingRestoreRef.current = null
        } else if (!hasMoreMessages) {
          // No more messages to load — message was probably deleted or from a different batch
          pendingRestoreRef.current = null
          // Scroll to bottom so user sees latest messages, not stranded at top
          scrollToBottom()
        } else {
          // Auto-trigger next pagination after cooldown
          setTimeout(() => {
            if (pendingRestoreRef.current && hasMoreMessages && !isLoadingOlderRef.current) {
              const innerEl = listRef.current
              if (innerEl) {
                prevScrollHeightRef.current = innerEl.scrollHeight
                // Capture anchor for reliable scroll preservation
                const firstVis = innerEl.querySelector('[data-message-id]')
                if (firstVis) {
                  anchorMsgRef.current = {
                    id: firstVis.getAttribute('data-message-id'),
                    offsetTop: firstVis.getBoundingClientRect().top - innerEl.getBoundingClientRect().top
                  }
                }
                isLoadingOlderRef.current = true
                loadOlderMessages()
              }
            }
          }, 250)
        }
      }

      prevMsgCountRef.current = msgCount
      prevFirstMsgIdRef.current = firstMsgId
      return
    }

    if (channelChanged || msgCount !== prevMsgCountRef.current || lastMsgId !== prevLastMsgIdRef.current) {
      // On channel switch, save position for the PREVIOUS channel before restoring new one
      if (channelChanged && prevChannelIdRef.current) {
        // Flush any pending debounced save to avoid stale writes
        clearTimeout(scrollSaveTimerRef.current)
        // CRITICAL: Use the live snapshot captured BEFORE render, not the current DOM
        // (which already has the NEW channel's messages at this point in useLayoutEffect)
        const snapshot = liveScrollSnapshotRef.current
        if (snapshot && snapshot.channelId === prevChannelIdRef.current) {
          const key = `paradigm_scroll_${prevChannelIdRef.current}`
          try {
            if (snapshot.atBottom) {
              sessionStorage.setItem(key, JSON.stringify({ atBottom: true }))
            } else {
              sessionStorage.setItem(key, JSON.stringify({
                atBottom: false,
                messageId: snapshot.messageId,
                offsetFromTop: snapshot.offsetFromTop
              }))
            }
          } catch {}
        } else {
          // Fallback: snapshot was from a different channel or missing
        }
        liveScrollSnapshotRef.current = null
        // Clear pending restore from old channel and reset pagination guards
        pendingRestoreRef.current = null
        isLoadingOlderRef.current = false
        scrollCooldownRef.current = false
      }

      // On channel switch or first load, try to restore saved position
      if (channelChanged || !restoredScrollRef.current) {
        restoredScrollRef.current = true
        const restoreChannelId = activeChannel?.id
        // Defer restore so DOM is fully rendered — use setTimeout to avoid
        // racing with prepend useLayoutEffect adjustments
        setTimeout(() => {
          // Guard: if channel changed again before this fires, abort
          if (activeChannel?.id !== restoreChannelId) {
            return
          }
          // Priority 1: scroll to unread divider if there are unreads
          if (unreadDividerMsgId && scrollToUnreadDivider()) {
            return
          }
          // Priority 2: restore saved scroll position
          if (!restoreScrollPosition()) {
            // Priority 3: fall back to bottom
            scrollToBottom()
          }
        }, 0)
      } else {
        // New message arrived — only scroll to bottom if already near bottom.
        // Smooth scroll so the existing messages glide up to make room for
        // the new one instead of snapping, iMessage-style.
        const el = listRef.current
        if (el) {
          const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150
          if (atBottom) {
            scrollToBottom(true)
          }
        }
      }
      prevMsgCountRef.current = msgCount
      prevLastMsgIdRef.current = lastMsgId
      prevFirstMsgIdRef.current = firstMsgId
      prevChannelIdRef.current = activeChannel?.id
    }
  }, [activeMessages, isInHuddle, activeChannel?.id, msgCount, lastMsgId, firstMsgId, unreadDividerMsgId])

  // Build a userId -> username map from all visible messages + channel members + workspace members
  const userNameMap = {}
  for (const member of (members || [])) {
    if (member.user_id && member.username) {
      userNameMap[member.user_id] = member.username
    }
  }
  for (const member of (activeChannelMembers || [])) {
    if (member.user_id && member.username) {
      userNameMap[member.user_id] = member.username
    }
  }
  for (const msg of activeMessages) {
    if (msg.sender?.id && msg.sender?.username) {
      userNameMap[msg.sender.id] = msg.sender.username
    }
  }
  if (currentUser?.id) userNameMap[currentUser.id] = currentUser.username || 'You'

  function resolveUsername(userId) {
    return userNameMap[userId] || userId?.slice(0, 8) || '?'
  }

  function flashReaction(msgId) {
    setJustReactedMsgId(msgId)
    clearTimeout(flashTimeoutRef.current)
    flashTimeoutRef.current = setTimeout(() => setJustReactedMsgId(null), 700)
  }

  function handleReactionClick(msgId, emoji, userIds) {
    if (userIds && userIds.includes(currentUser?.id)) {
      removeReaction(msgId, emoji)
    } else {
      addReaction(msgId, emoji)
      flashReaction(msgId)
      // Every reaction feeds the tally, not just the ones picked from the
      // panel — otherwise the quick row could never learn from its own use.
      trackEmojiUsage(emoji)
    }
  }

  function handlePickerSelect(msgId, emoji) {
    // (the panel tallies its own picks — see EmojiPickerPanel.handleSelect)
    addReaction(msgId, emoji)
    setPickerMsgId(null)
    flashReaction(msgId)
  }

  function handleImageClick(src, alt) {
    setLightbox({ src, alt })
  }

  function handleForward(targetChannelId, message) {
    forwardMessage(targetChannelId, message)
    setForwardMsg(null)
  }

  function handleMarkUnread(msg) {
    if (activeChannel?.id && msg?.id) {
      markAsUnread(activeChannel.id, msg.id)
    }
  }

  function handleStartEdit(msg) {
    setEditingMessage(msg)
  }

  function handleDeleteMessage(messageId) {
    if (deleteMessage) deleteMessage(messageId)
  }

  const pinnedMessageIds = useMemo(() => new Set((pinnedMessages || []).map(p => p.message_id)), [pinnedMessages])

  function handlePinMessage(messageId) {
    if (pinnedMessageIds.has(messageId)) {
      unpinMessage(messageId)
    } else {
      pinMessage(messageId)
    }
  }

  function handleReply(msg) {
    setReplyingToMessage(msg)
  }

  function handleScrollToMessage(msgId) {
    const el = document.querySelector(`[data-message-id="${msgId}"]`)
    if (el) jumpToMessageEl(el)
  }

  const messageIndexMap = {}
  for (let i = 0; i < activeMessages.length; i++) {
    const message = activeMessages[i]
    if (message?.id) messageIndexMap[message.id] = i
  }

  function getReceiptStatus(msg) {
    if (msg.sender_id !== currentUser?.id) return null
    if (!msg?.id || msg.id.startsWith('temp-')) return null
    const recipients = (activeChannelMembers || []).filter(m => m.user_id && m.user_id !== currentUser?.id)
    if (recipients.length === 0) return null

    const messageIndex = messageIndexMap[msg.id]
    if (messageIndex === undefined) return null

    const readBy = []
    const unreadBy = []
    for (const recipient of recipients) {
      const receipt = readReceipts?.[recipient.user_id]
      const lastReadId = receipt?.last_read_message_id
      const lastReadIdx = lastReadId ? messageIndexMap[lastReadId] : undefined
      const hasReadById = lastReadIdx !== undefined && lastReadIdx >= messageIndex
      // Fallback when last_read_message_id is outside the currently loaded message window.
      const hasReadByTime = !hasReadById && receipt?.read_at
        ? new Date(receipt.read_at).getTime() >= new Date(msg.timestamp).getTime()
        : false
      const hasRead = hasReadById || hasReadByTime
      const name = recipient.username || recipient.user_id?.slice(0, 8) || 'Unknown'
      if (hasRead) readBy.push(name)
      else unreadBy.push(name)
    }

    return {
      // Blue only when all recipients have read this message.
      isRead: unreadBy.length === 0,
      readBy,
      unreadBy,
    }
  }

  const [expandedCalendarDate, setExpandedCalendarDate] = useState(null)

  // Group messages into day sections for sticky dividers
  const daySections = []
  let currentSection = null
  for (let idx = 0; idx < activeMessages.length; idx++) {
    const msg = activeMessages[idx]
    const prevMsg = idx > 0 ? activeMessages[idx - 1] : null
    const msgDate = new Date(msg.timestamp).toDateString()
    const prevDate = prevMsg ? new Date(prevMsg.timestamp).toDateString() : null
    const isNewDay = !prevMsg || msgDate !== prevDate

    if (isNewDay) {
      currentSection = { label: formatDayDivider(msg.timestamp), date: msgDate, messages: [] }
      daySections.push(currentSection)
    }
    currentSection.messages.push({ msg, idx, isNewDay })
  }

  // Mark each row's position in its run. Scoped per section because a day
  // boundary always ends a run — the capsule between them is a real break.
  for (const section of daySections) {
    const items = section.messages
    for (let i = 0; i < items.length; i++) {
      const withPrev = sameRun(items[i - 1]?.msg, items[i].msg, unreadDividerMsgId)
      const withNext = sameRun(items[i].msg, items[i + 1]?.msg, unreadDividerMsgId)
      items[i].group = withPrev ? (withNext ? 'mid' : 'tail') : (withNext ? 'head' : 'solo')
    }
  }

  const daysWithMessages = useMemo(() => new Set(daySections.map(s => s.date)), [daySections])

  function handleScrollToDate(dateString) {
    const sectionEl = listRef.current?.querySelector(`[data-date="${dateString}"]`)
    if (sectionEl && listRef.current) {
      const container = listRef.current
      const targetTop = sectionEl.offsetTop - container.offsetTop - 16
      container.scrollTo({ top: targetTop, behavior: 'smooth' })
    }
    setExpandedCalendarDate(null)
  }

  // Scroll-up pagination: load older messages when near top
  // Uses local refs to prevent rapid-fire re-triggering (the loadingOlder prop from context
  // may not update synchronously, causing the scroll handler to fire multiple loads)

  function handleListScroll(e) {
    const el = e.target


    // Track if user is at bottom — used by ResizeObserver to auto-scroll on image load
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    wasAtBottomRef.current = distFromBottom < 50
    setShowScrollToBottom(distFromBottom > 150)

    // Pagination guard: check local refs in addition to context's loadingOlder
    if (el.scrollTop < 300 && hasMoreMessages && !loadingOlder && !isLoadingOlderRef.current && !scrollCooldownRef.current) {
      // Save scrollHeight BEFORE loading so useLayoutEffect can compute delta
      prevScrollHeightRef.current = el.scrollHeight
      // Also capture an anchor message — more reliable than scrollHeight delta
      const firstVisible = el.querySelector('[data-message-id]')
      if (firstVisible) {
        anchorMsgRef.current = {
          id: firstVisible.getAttribute('data-message-id'),
          offsetTop: firstVisible.getBoundingClientRect().top - el.getBoundingClientRect().top
        }
      }
      isLoadingOlderRef.current = true
      loadOlderMessages()
    }

    // Skip snapshot+save when WE drove the scroll (restore, scrollToBottom, prepend
    // preserve, etc.). Otherwise drift from imprecise restores compounds across visits.
    if (programmaticScrollRef.current) return

    // Capture live scroll snapshot on EVERY (user-driven) scroll event.
    // This is critical: on channel switch, useLayoutEffect runs AFTER render so the DOM
    // already has the NEW channel's messages. We need to capture position BEFORE that happens.
    if (!pendingRestoreRef.current && activeChannel?.id) {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50
      if (atBottom) {
        liveScrollSnapshotRef.current = { channelId: activeChannel.id, atBottom: true }
      } else {
        const first = firstVisibleMessage(el)
        if (first) liveScrollSnapshotRef.current = { channelId: activeChannel.id, atBottom: false, ...first }
      }
    }

    // Debounced save of scroll position to sessionStorage (only if not in pending restore mode)
    if (!pendingRestoreRef.current) {
      clearTimeout(scrollSaveTimerRef.current)
      scrollSaveTimerRef.current = setTimeout(saveScrollPosition, 200)
    }
  }

  return (
    <div
      ref={listRef}
      className={`block__body message-list${isInHuddle ? ' message-list-huddle' : ''}${bubbleMode && activeChannel?.is_dm && !activeChannel?.is_group_dm ? ' bubble-dm' : ''}${pinnedMessages.length > 0 ? ' has-pins' : ''}`}
      onScroll={handleListScroll}
      data-qh-title="Message history"
      data-qh-body="The conversation history for this channel. Scroll up to load earlier messages."
    >
      {/* (the in-scroll channel intro retired 2026-08-16, the owner's second
          word — the LITERAL header carries the name and the ledger now) */}
      {/* These elements must NOT be scroll anchors — only .message elements anchor */}
      {loadingOlder && (
        <div className="scroll-no-anchor" style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-muted)', fontSize: 'var(--ofself-text-base)' }}>
          Loading older messages...
        </div>
      )}
      {pinnedMessages.length > 0 && (
        <PinnedMessagesPanel
          pinnedMessages={pinnedMessages}
          onUnpin={(msgId) => unpinMessage(msgId)}
          onScrollTo={(msgId) => {
            const el = document.querySelector(`[data-message-id="${msgId}"]`)
            if (el) jumpToMessageEl(el)
          }}
          resolveUsername={resolveUsername}
        />
      )}
      {daySections.map((section) => (
        <div className="day-section" key={section.date} data-date={section.date}>
          <DayDividerCalendar
            label={section.label}
            sectionDate={section.date}
            daysWithMessages={daysWithMessages}
            isExpanded={expandedCalendarDate === section.date}
            onToggle={() => setExpandedCalendarDate(expandedCalendarDate === section.date ? null : section.date)}
            onNavigateToDate={handleScrollToDate}
          />
          {section.messages.map(({ msg, idx, group }) => {
            if (msg.sender_type === 'agent' || msg.sender_type === 'plugin') {
              const agentMeta     = msg.agent_meta || {}
              const agentName     = agentMeta.agent_name || agentMeta.display_name || agentMeta.sender_name || 'Agent'
              const agentInitials = agentName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'AI'
              const normalizedAgentMsg = {
                ...msg,
                sender: { username: agentName, initials: agentInitials, color: 'var(--accent)' },
                agent_meta: agentMeta,
                auditTrail: true,
              }
              // Pass through MessageRow so context-menu / Reply / Open Thread work identically
              return (
                <React.Fragment key={msg._key || msg.id}>
                  <MessageRow
                    msg={normalizedAgentMsg}
                    idx={idx}
                    group={group}
                    quickEmojis={quickEmojis}
                    pickerMsgId={pickerMsgId}
                    setPickerMsgId={setPickerMsgId}
                    onReactionClick={addReaction}
                    onPickerSelect={(emoji) => addReaction(normalizedAgentMsg.id, emoji, [])}
                    openThread={openThread}
                    currentUser={currentUser}
                    onImageClick={handleImageClick}
                    onForwardClick={setForwardMsg}
                    onMarkUnread={handleMarkUnread}
                    onStartEdit={() => {}}
                    onDeleteMessage={deleteMessage}
                    onPinMessage={pinMessage}
                    pinnedMessageIds={pinnedMessageIds}
                    resolveUsername={resolveUsername}
                    onReply={handleReply}
                    onScrollToMessage={handleScrollToMessage}
                    receiptStatus={null}
                    activeChannel={activeChannel}
                    justArrived={arrivingIds.has(msg._key || msg.id)}
                  />
                </React.Fragment>
              )
            }

            return (
              <React.Fragment key={msg._key || msg.id}>
                {unreadDividerMsgId && (() => {
                  // Show "New messages" divider after the last-read message
                  // Case 1: The last-read message is the previous message in the list
                  const prevIsLastRead = idx > 0 && activeMessages[idx - 1]?.id === unreadDividerMsgId && msg.id !== unreadDividerMsgId && msg.sender_id !== currentUser?.id
                  // Case 2: The last-read message is older than ALL loaded messages
                  // (not in the current batch) — show divider at the very top
                  const lastReadNotLoaded = idx === 0 && !activeMessages.some(m => m.id === unreadDividerMsgId) && msg.sender_id !== currentUser?.id
                  return (prevIsLastRead || lastReadNotLoaded) ? (
                    <div className="unread-divider">
                      <span className="unread-divider-label">New messages</span>
                    </div>
                  ) : null
                })()}
                <div className={`message-select-row${bulkEditMode ? ' message-select-row-active' : ''}`} onClick={bulkEditMode ? () => {
                  setBulkSelectedMessages(prev => {
                    const next = new Set(prev)
                    if (next.has(msg.id)) next.delete(msg.id); else next.add(msg.id)
                    return next
                  })
                } : undefined}>
                  {bulkEditMode && (
                    <div className={`message-select-circle${bulkSelectedMessages.has(msg.id) ? ' selected' : ''}`}>
                      {bulkSelectedMessages.has(msg.id) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                    </div>
                  )}
                  <MessageRow
                    msg={msg}
                    idx={idx}
                    group={group}
                    quickEmojis={quickEmojis}
                    pickerMsgId={pickerMsgId}
                    setPickerMsgId={setPickerMsgId}
                    onReactionClick={handleReactionClick}
                    onPickerSelect={handlePickerSelect}
                    openThread={openThread}
                    currentUser={currentUser}
                    onImageClick={handleImageClick}
                    onForwardClick={setForwardMsg}
                    onMarkUnread={handleMarkUnread}
                    onStartEdit={handleStartEdit}
                    onDeleteMessage={handleDeleteMessage}
                    onPinMessage={handlePinMessage}
                    pinnedMessageIds={pinnedMessageIds}
                    resolveUsername={resolveUsername}
                    onReply={handleReply}
                    onScrollToMessage={handleScrollToMessage}
                    receiptStatus={getReceiptStatus(msg)}
                    activeChannel={activeChannel}
                    justReacted={msg.id === justReactedMsgId}
                    justArrived={arrivingIds.has(msg._key || msg.id)}
                  />
                </div>
              </React.Fragment>
            )
          })}
        </div>
      ))}
      {typingUsers && typingUsers.length > 0 && (
        <div className="typing-indicator">
          <span className="typing-avatars">
            {typingUsers.slice(0, 3).map((u, i) => {
              const name = u?.username || (typeof u === 'string' ? u : '?')
              const uid = u?.user_id
              return (
                <UserAvatar
                  key={uid || name}
                  userID={uid}
                  initials={(name[0] || '?').toUpperCase()}
                  color={avatarColor(name)}
                  className="typing-avatar"
                  style={{ zIndex: 3 - i }}
                />
              )
            })}
          </span>
          <span className="typing-breath-dots">
            <span className="typing-dot-breathing" />
            <span className="typing-dot-breathing" />
            <span className="typing-dot-breathing" />
          </span>
        </div>
      )}
      <div ref={bottomRef} />
      <button
        className={`ofself-icon-btn ofself-icon-btn--chrome scroll-to-bottom-btn${showScrollToBottom ? ' visible' : ''}`}
        onClick={(e) => { e.stopPropagation(); scrollToBottom(true) }}
        aria-label="Scroll to bottom"
      >
        <ChevronDown size={64} />
      </button>
      {lightbox && createPortal(
        <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />,
        document.body
      )}
      {forwardMsg && (
        <ForwardModal
          message={forwardMsg}
          channels={channels}
          dmChannels={dmChannels || []}
          onForward={handleForward}
          onClose={() => setForwardMsg(null)}
        />
      )}
      {bulkEditMode && bulkSelectedMessages.size > 0 && (
        <div className="bulk-action-bar">
          <button className="bulk-action-btn bulk-action-delete" onClick={() => {
            if (confirm(`Delete ${bulkSelectedMessages.size} message${bulkSelectedMessages.size > 1 ? 's' : ''}?`)) {
              bulkSelectedMessages.forEach(id => deleteMessage(id))
              setBulkSelectedMessages(new Set())
              setBulkEditMode(false)
            }
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
            Delete
          </button>
          <span className="bulk-action-count">{bulkSelectedMessages.size} message{bulkSelectedMessages.size > 1 ? 's' : ''} selected</span>
          <button className="bulk-action-btn bulk-action-forward" onClick={() => {
            const selected = activeMessages.filter(m => bulkSelectedMessages.has(m.id))
            if (selected.length > 0) {
              const combined = selected.map(m => `**${m.sender?.username || 'Unknown'}**: ${m.content}`).join('\n\n')
              setForwardMsg({ ...selected[0], content: combined })
            }
            setBulkSelectedMessages(new Set())
            setBulkEditMode(false)
          }}>
            Forward
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 17 20 12 15 7" /><path d="M4 18v-2a4 4 0 0 1 4-4h12" /></svg>
          </button>
        </div>
      )}
    </div>
  )
}

export function MessageContent({ content }) {
  // Check if this is a forwarded message (starts with "> **Forwarded from")
  const lines = content.split('\n')
  const isForwarded = lines[0]?.startsWith('> **Forwarded from')

  if (isForwarded) {
    // Extract sender from first line: > **Forwarded from Username:**
    const senderMatch = lines[0].match(/>\s*\*\*Forwarded from (.+?):\*\*/)
    const senderName = senderMatch?.[1] || 'Unknown'
    const quotedLines = lines.slice(1).map(l => l.startsWith('> ') ? l.slice(2) : l)
    const quotedContent = quotedLines.join('\n').trim()

    return (
      <div className="forwarded-message">
        <div className="forwarded-label">
          <CornerUpRight size={12} />
          <span>Forwarded from <strong>{senderName}</strong></span>
        </div>
        <div className="forwarded-content">
          <InlineContent content={quotedContent} />
        </div>
      </div>
    )
  }

  return <InlineContent content={content} />
}

function InlineContent({ content }) {
  // Handle code blocks first (```...```)
  const codeBlockRegex = /```([\s\S]*?)```/g
  const segments = []
  let lastIdx = 0
  let match

  while ((match = codeBlockRegex.exec(content)) !== null) {
    if (match.index > lastIdx) {
      segments.push({ type: 'text', value: content.slice(lastIdx, match.index) })
    }
    segments.push({ type: 'codeblock', value: match[1].replace(/^\n|\n$/g, '') })
    lastIdx = match.index + match[0].length
  }
  if (lastIdx < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIdx) })
  }

  return (
    <>
      {segments.map((seg, si) => {
        if (seg.type === 'codeblock') {
          return <pre key={si} className="msg-codeblock"><code>{seg.value}</code></pre>
        }
        return <InlineText key={si} text={seg.value} />
      })}
    </>
  )
}

function InlineText({ text }) {
  // Split into lines to handle block-level elements (lists)
  const lines = text.split('\n')
  const result = []
  let listItems = []
  let listType = null // 'ul' or 'ol'
  let tableLines = []

  function flushTable() {
    if (tableLines.length < 2) {
      // Not enough rows for a table — emit as plain text
      tableLines.forEach((l, ti) => result.push(<span key={`traw-${result.length}-${ti}`}>{l}<br /></span>))
      tableLines = []
      return
    }
    const isSep = (row) => /^[\s|:\-]+$/.test(row)
    const parseRow = (row) => row.replace(/^\||\|$/g, '').split('|').map(c => c.trim())
    const rows = tableLines.filter(l => !isSep(l))
    const headerCells = parseRow(rows[0])
    const bodyRows = rows.slice(1)
    result.push(
      <table key={`tbl-${result.length}`} className="msg-table">
        <thead>
          <tr>{headerCells.map((c, ci) => <th key={ci}><InlineSpan text={c} /></th>)}</tr>
        </thead>
        <tbody>
          {bodyRows.map((r, ri) => (
            <tr key={ri}>{parseRow(r).map((c, ci) => <td key={ci}><InlineSpan text={c} /></td>)}</tr>
          ))}
        </tbody>
      </table>
    )
    tableLines = []
  }

  function flushList() {
    if (listItems.length > 0) {
      if (listType === 'ol') {
        result.push(
          <ol key={`list-${result.length}`} className="msg-list" start={listStartNum}>
            {listItems.map((item, i) => <li key={i} value={listItemNums[i]}><InlineSpan text={item} /></li>)}
          </ol>
        )
      } else {
        result.push(
          <ul key={`list-${result.length}`} className="msg-list msg-list-dash">
            {listItems.map((item, i) => <li key={i}><InlineSpan text={item} /></li>)}
          </ul>
        )
      }
      listItems = []
      listItemNums = []
      listStartNum = 1
      listType = null
    }
  }
  let listItemNums = []
  let listStartNum = 1

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Headings: #, ##, ###
    const headingMatch = line.match(/^(#{1,3})\s+(.+)/)
    if (headingMatch) {
      flushList()
      if (tableLines.length > 0) flushTable()
      const level = headingMatch[1].length
      const Tag = `h${level + 2}` // h3, h4, h5 to avoid clashing with page headings
      result.push(<Tag key={`h-${i}`} className={`msg-heading msg-h${level}`}><InlineSpan text={headingMatch[2]} /></Tag>)
      continue
    }

    // Table rows: lines that start (and optionally end) with |
    if (line.trimStart().startsWith('|')) {
      flushList()
      tableLines.push(line)
      continue
    } else if (tableLines.length > 0) {
      flushTable()
    }

    const ulMatch = line.match(/^[-*]\s+(.+)/)
    const olMatch = line.match(/^\d+\.\s+(.+)/)
    const checkMatch = line.match(/^- \[([ x])\]\s+(.+)/)

    if (checkMatch) {
      flushList()
      const checked = checkMatch[1] === 'x'
      result.push(
        <div key={`check-${i}`} className={`checklist-item ${checked ? 'checked' : ''}`}>
          <InlineSpan text={checkMatch[2]} />
        </div>
      )
    } else if (ulMatch) {
      if (listType !== 'ul') flushList()
      listType = 'ul'
      listItems.push(ulMatch[1])
    } else if (olMatch) {
      if (listType !== 'ol') flushList()
      listType = 'ol'
      const num = parseInt(olMatch[0], 10)
      if (listItems.length === 0) listStartNum = num
      listItemNums.push(num)
      listItems.push(olMatch[1])
    } else {
      flushList()
      if (line === '') {
        if (i > 0 && i < lines.length - 1) result.push(<br key={`br-${i}`} />)
      } else {
        if (result.length > 0 && i > 0) result.push(<br key={`br-${i}`} />)
        result.push(<InlineSpan key={`line-${i}`} text={line} />)
      }
    }
  }
  if (tableLines.length > 0) flushTable()
  flushList()

  return <>{result}</>
}

function AnimatedText({ text }) {
  return <>{text}</>
}

function InlineSpan({ text }) {
  // Parse inline formatting: **bold**, _italic_, ~~strike~~, `code`, [link](url)
  // Underscores require word boundary (space/start/end) so variable_names aren't mangled
  const regex = /(\*\*(.+?)\*\*|(?:^|(?<=\s))__(.+?)__(?=\s|$)|(?:^|(?<=\s))_([^\s_].*?[^\s_])_(?=\s|$)|~~(.+?)~~|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|(@\w+)|(https?:\/\/[^\s<]+[^\s<.,;:!?"'\])}>]))/g
  const parts = []
  let lastIdx = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<AnimatedText key={lastIdx} text={text.slice(lastIdx, match.index)} />)
    }
    const key = match.index
    if (match[2]) {
      parts.push(<strong key={key}>{match[2]}</strong>)
    } else if (match[3]) {
      parts.push(<span key={key} style={{ textDecoration: 'underline' }}>{match[3]}</span>)
    } else if (match[4]) {
      parts.push(<em key={key}>{match[4]}</em>)
    } else if (match[5]) {
      parts.push(<del key={key}>{match[5]}</del>)
    } else if (match[6]) {
      parts.push(<code key={key} className="msg-inline-code">{match[6]}</code>)
    } else if (match[7] && match[8]) {
      parts.push(<a key={key} href={match[8]} target="_blank" rel="noopener noreferrer" className="msg-link">{match[7]}</a>)
    } else if (match[9]) {
      const mention = match[9]
      parts.push(
        <span key={key} className="mention-highlight">
          {mention}
        </span>
      )
    } else if (match[10]) {
      const url = match[10]
      parts.push(<a key={key} href={url} target="_blank" rel="noopener noreferrer" className="msg-link">{url}</a>)
    }
    lastIdx = match.index + match[0].length
  }
  if (lastIdx < text.length) {
    parts.push(<AnimatedText key={lastIdx} text={text.slice(lastIdx)} />)
  }

  return <>{parts}</>
}
